import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { getErrorMessage } from '@/shared/api/errors';
import * as api from './reportsApi';
import type { AddCommentPayload, CreateSharedLinkPayload } from '../types/reports';

export function useReportCampaignsQuery() {
  return useQuery({
    queryKey: queryKeys.reports.campaignsDropdown(),
    queryFn: api.fetchReportCampaigns,
    refetchOnWindowFocus: false,
  });
}

export function useSharedLinksQuery() {
  return useQuery({
    queryKey: queryKeys.reports.list(),
    queryFn: api.fetchSharedLinks,
    refetchOnWindowFocus: false,
  });
}

export function useCreateSharedLinkMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSharedLinkPayload) => api.createSharedLink(payload),
    onSuccess: (data) => {
      toast.success(data?.message || 'Shared link created');
      void qc.invalidateQueries({ queryKey: queryKeys.reports.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to create shared link'));
    },
  });
}

export function useDeleteSharedLinkMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => api.deleteSharedLink(shareId),
    onSuccess: (data) => {
      toast.success(data?.message || 'Shared link deleted');
      void qc.invalidateQueries({ queryKey: queryKeys.reports.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to delete shared link'));
    },
  });
}

export function useReportTabDataQuery(
  token?: string,
  activeTab = 'analytics',
  params?: { page?: number; limit?: number; date?: string; slot?: string },
) {
  return useQuery({
    queryKey: queryKeys.reports.tabsData(token ?? '', activeTab),
    queryFn: () => api.fetchReportTabData(token as string, activeTab, params),
    enabled: Boolean(token),
    refetchOnWindowFocus: false,
  });
}

export function useInfiniteReportProgressQuery(
  token?: string,
  filters?: { date?: string; slot?: string; screenId?: string },
  limit = 48,
) {
  return useInfiniteQuery({
    queryKey: [
      ...queryKeys.reports.root,
      'progress-infinite',
      token ?? '',
      filters ?? {},
    ],
    queryFn: ({ pageParam = 1 }) =>
      api.fetchReportTabData(token as string, 'progress', {
        page: pageParam as number,
        limit,
        date: filters?.date || undefined,
        slot: filters?.slot || undefined,
        screenId: filters?.screenId || undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const timings = lastPage?.data?.playTimings || [];
      const total = lastPage?.data?.totalPlayTimings;
      const loadedCount = allPages.reduce(
        (sum, p) => sum + (p?.data?.playTimings?.length || 0),
        0,
      );

      if (total != null) {
        return loadedCount < total ? allPages.length + 1 : undefined;
      }
      return timings.length >= limit ? allPages.length + 1 : undefined;
    },
    enabled: Boolean(token),
    refetchOnWindowFocus: false,
  });
}

export function useAddReportCommentMutation(token?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddCommentPayload) =>
      api.addReportComment(token as string, payload),
    onSuccess: (data) => {
      toast.success(data?.message || 'Comment added');
      void qc.invalidateQueries({ queryKey: queryKeys.reports.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to add comment'));
    },
  });
}
